import { Card, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import {
    Autocomplete, Box, Button, FormField, SelectMultiLanguage,
    Typography, useCommonComponentContext, IPropDetailContainer,
} from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import CategoryService from "../../services/message/Category.service";

import { IRecordCategory } from "./list";
import { ICodename } from "../../commons/interfaces";
import { Constants } from "../../constants/constants";
import { CategoryType, Mode } from "../../constants/enums";

type IErrorCategory = { [key in keyof IRecordCategory]?: string };

const CategoryEditContainer = (props: IPropDetailContainer) => {
    const Strings = useStrings();

    const { serviceCode, tenantCode, language, listMultiLanguage, onError, onSuccess, onHideLoading, onShowLoading } = useCommonComponentContext();

    const [isEdited, setIsEdited] = useState<boolean>(false);
    const [parentCategoryList, setParentCategoryList] = useState<ICodename[]>([]);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(Constants.DefaultLanguage);

    const [dataError, setDataError] = useState<IErrorCategory>({} as IErrorCategory);
    const [dataDetail, setDataDetail] = useState<IRecordCategory>({} as IRecordCategory);

    const categoryTypes = [
        {
            code: CategoryType.COURSE,
            name: Strings.CATEGORY.COURSE,
        },
        {
            code: CategoryType.POST,
            name: Strings.CATEGORY.POST,
        },
        {
            code: CategoryType.QUESTION,
            name: Strings.CATEGORY.QUESTION,
        },
    ];

    useEffect(() => {
        setSelectedLanguage(language);
    }, [language]);

    useEffect(() => {
        (async () => {
            try {
                onShowLoading();

                await getAllCategory({ id: props.idDetail, type: CategoryType.COURSE });

                if (!Helpers.isNullOrEmpty(props.idDetail)) {
                    const result = await CategoryService.getDetail(props.idDetail);
                    const newDetail = {
                        id: result?.id,
                        type: result?.type,
                        name: result?.name,
                        parentCategory: Number(result?.parentCategory) === 0 ? undefined : result?.parentCategory,
                    };
                    setDataDetail(newDetail);
                }
            } catch (error) {
                const err = Helpers.renderExceptionError(error);
                onError(err);
            } finally {
                onHideLoading();
            }
        })();
    }, [props.idDetail]);

    const getAllCategory = async (props: { id?: string; type: CategoryType }) => {
        try {
            let newList: ICodename[] = [];

            const result = await CategoryService.getAll({
                serviceCode: serviceCode,
                tenantCode: tenantCode,
                type: props.type,
            });

            [...(result || [])].forEach((e: any) => {
                if (e.id !== props.id) {
                    newList.push({
                        code: e.id,
                        name: Helpers.renderValueByLanguage({ data: e.name, currentLanguage: language }),
                    });
                }
            });

            setParentCategoryList(newList);
        } catch (error) {
            setParentCategoryList([]);
        }
    };

    const onValueChange = async (value: any, key: any, isMultiLang?: boolean) => {
        let dataTemp: any = { ...dataDetail };
        let dataErrTemp: any = { ...dataError };

        dataErrTemp[key] = undefined;

        if (isMultiLang) {
            dataTemp[key] = {
                ...dataTemp[key],
                value: {
                    ...dataTemp[key]?.value,
                    [selectedLanguage]: value,
                },
            };
        } else {
            dataTemp[key] = value;
            if (key === "type") {
                onShowLoading();

                await getAllCategory({ id: dataDetail?.id, type: value });

                dataTemp["parentCategory"] = undefined;
                dataErrTemp["parentCategory"] = undefined;

                onHideLoading();
            }
        }

        setIsEdited(true);
        setDataDetail(dataTemp);
        setDataError(dataErrTemp);
    };

    const checkedValidate = () => {
        let isChecked = true;
        let dataErrTemp: any = { ...dataError };

        if (Helpers.isNullOrEmpty(dataDetail?.type)) {
            isChecked = false;
            dataErrTemp["type"] = Strings.Common.REQUIRED;
        }
        // if (Helpers.isNullOrEmpty(dataDetail?.parentCategory)) {
        //     isChecked = false;
        //     dataErrTemp["parentCategory"] = Strings.Common.REQUIRED;
        // }
        if (Helpers.isNullOrEmpty(dataDetail?.name?.value?.[selectedLanguage])) {
            isChecked = false;
            dataErrTemp["name"] = Strings.Common.REQUIRED;
        }

        if (!isChecked) {
            setDataError(dataErrTemp);
        }

        return isChecked;
    };

    const handleCreateOrUpdate = async () => {
        if (props.mode === Mode.View) {
            props.onChangeMode(Mode.Update);
        } else {
            if (!checkedValidate()) {
                return;
            } else {
                try {
                    onShowLoading();

                    let dataSubmit = {
                        type: dataDetail.type,
                        name: Helpers.getDataMultiLanguage({
                            data: dataDetail?.name,
                            currentLanguage: language,
                            listLanguage: listMultiLanguage,
                        }),
                        parentCategory: dataDetail.parentCategory,
                        serviceCode: serviceCode,
                        tenantCode: tenantCode,
                    };

                    if (Helpers.isNullOrEmpty(dataDetail?.id)) {
                        const resultId = await CategoryService.create(dataSubmit);

                        setIsEdited(false);
                        setDataDetail({ ...dataDetail, id: resultId });

                        onSuccess(Strings.CATEGORY.CREATE_SUCCESS);

                        props.onGoBack();
                    } else {
                        await CategoryService.update({
                            ...dataSubmit,
                            id: dataDetail.id,
                        });

                        setIsEdited(false);

                        props.onChangeMode(Mode.View);

                        onSuccess(Strings.CATEGORY.UPDATE_SUCCESS);
                    }

                    onHideLoading();
                } catch (error: any) {
                    onHideLoading();
                    const err = Helpers.renderExceptionError(error);
                    onError(err);
                }
            }
        }
    };

    const renderBasicInfo = () => (
        <Card>
            <Box p={2}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h5">{Strings.CATEGORY.TITLE_INFO}</Typography>
                    <SelectMultiLanguage
                        defaultLanguage={selectedLanguage}
                        onChangeLanguage={(value) => {
                            setSelectedLanguage(value);
                            setDataError({ ...dataError, name: "" });
                        }}
                    />
                </Box>
                <Grid container spacing={3} p={2}>
                    <Grid item xs={12} md={12}>
                        <Autocomplete
                            required
                            mode={props.mode}
                            data={categoryTypes || []}
                            label={Strings.CATEGORY.TYPE}
                            errorMessage={dataError?.type}
                            placeholder={Strings.CATEGORY.SELECT_TYPE}
                            defaultValue={!Helpers.isNullOrEmpty(dataDetail?.type) ? dataDetail.type : undefined}
                            onChange={(value: number) => onValueChange(value, "type")}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Autocomplete
                            mode={props.mode}
                            data={parentCategoryList || []}
                            label={Strings.CATEGORY.PARENT_CATEGORY}
                            errorMessage={dataError?.parentCategory}
                            defaultValue={dataDetail?.parentCategory}
                            placeholder={Strings.CATEGORY.SELECT_PARENT_CATEGORY}
                            onChange={(value: string) => onValueChange(value, "parentCategory")}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <FormField
                            required
                            maxLength={255}
                            mode={props.mode}
                            label={Strings.CATEGORY.NAME}
                            errorMessage={dataError?.name}
                            placeholder={Strings.CATEGORY.ENTER_NAME}
                            defaultValue={dataDetail?.name?.value?.[selectedLanguage]}
                            onBlur={(value: string) => onValueChange(value, "name", true)}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Card>
    );

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "end" }}>
                        <Button
                            color={"secondary"}
                            onClick={() => {
                                if (isEdited) {
                                    Helpers.showConfirmAlert(Strings.Common.CANCEL_ACTION_MESSAGE, props.onGoBack);
                                } else {
                                    props.onGoBack();
                                }
                            }}
                        >
                            {Strings.Common.GO_BACK}
                        </Button>
                        <Button
                            sx={{ ml: 2 }}
                            color={"dark"}
                            onClick={() => {
                                handleCreateOrUpdate();
                            }}
                        >
                            {props.mode === Mode.View ? Strings.Common.EDIT : Strings.Common.SAVE}
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    {renderBasicInfo()}
                </Grid>
            </Grid>
        </Box>
    );
};

export default CategoryEditContainer;
